import { SourcingStageStat } from 'api-clients/monolith';
import _round from 'lodash/round';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { PerformanceStatusPill } from '@/components/base/performance-status-pill';
import { ConversionStats } from '@/components/ui/conversion-stats';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useOpeningContext } from '@/contexts/opening-context';
import { durationInDays, isWithinLast24Hours, toDate } from '@/utils/date-util';

import { AutomationActionPill } from './automation-action-pill';
import { HireStagesHoverCard } from './hire-stages-hover-card';

interface StageProps {
  stage: SourcingStageStat;
  lastAutomationTriggerAt: string | null;
  isHiringGoalMet: boolean;
}

export function Stage({
  stage,
  lastAutomationTriggerAt,
  isHiringGoalMet,
}: StageProps) {
  const { hiringGoal } = useOpeningContext();

  const {
    title: sourceStage,
    hire_stage_title: hireStage,
    applicants_stats: applicantStats,
    performance_status: status,
    is_automated: isAutomated,
    stage_type: stageType,
    performance_status_updated_at: statusUpdatedAt,
    conversion_stats: conversionStats,
    applicants_hired_in_stages: applicantsHiredInStages,
  } = stage;
  const { expected, actual } = applicantStats;

  const statusUpdatedAtDate = toDate(statusUpdatedAt);
  const automationTriggeredAt = toDate(lastAutomationTriggerAt);

  const statusChangedRecently =
    statusUpdatedAtDate && isWithinLast24Hours(statusUpdatedAtDate);
  const automationTriggeredRecently =
    automationTriggeredAt && isWithinLast24Hours(automationTriggeredAt);

  const showAutomationTriggeredRecently =
    automationTriggeredRecently &&
    statusChangedRecently &&
    isAutomated &&
    ((!isHiringGoalMet && stageType === 'landing') ||
      (isHiringGoalMet && stageType === 'hired'));

  const expectedByToday = useMemo(() => {
    if (!hiringGoal || !expected) return 0;

    const perDayExpected = expected / hiringGoal.duration;
    const daysElapsed = durationInDays(hiringGoal.start_date, new Date());

    return _round(perDayExpected * daysElapsed);
  }, [hiringGoal, expected]);

  return (
    <>
      <div className="bg-fx-neutral-200 h-px w-full" />

      <div className="flex justify-between">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <Typography variant="text">{sourceStage}</Typography>
            {stageType === 'hired' && applicantsHiredInStages.length > 0 ? (
              <HireStagesHoverCard
                applicantsHiredInStages={applicantsHiredInStages}
              />
            ) : (
              <Typography variant="body1" className="!text-fx-neutral-400">
                {`(${hireStage})`}
              </Typography>
            )}
          </div>

          <Typography variant="small1" className="!text-fx-neutral-600">
            {`${actual ?? 0} / ${expected ?? '-'} applicants`}
          </Typography>

          {stageType !== 'hired' && hiringGoal?.is_current && (
            <div className="flex">
              <Typography
                variant="small1"
                className="!text-fx-neutral-600 mr-1"
              >
                <FormattedMessage defaultMessage="Conversion to hire:" />
              </Typography>
              <ConversionStats
                stats={conversionStats}
                typographyVariant="small1"
                className="!text-fx-neutral-600"
              />
            </div>
          )}
        </div>

        {hiringGoal?.is_current && (
          <PerformanceStatusPill
            status={status}
            criteria="apply"
            expected={expectedByToday}
            actual={actual}
          />
        )}
      </div>

      {showAutomationTriggeredRecently && (
        <AutomationActionPill status={status} />
      )}
    </>
  );
}

Stage.Skeleton = function StageSkeleton() {
  return (
    <>
      <div className="bg-fx-neutral-200 h-px w-full" />
      <div className="flex justify-between">
        <div className="flex flex-col gap-3">
          <div className="flex h-5 items-center gap-1">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="w-15 h-4" />
          </div>
          <Skeleton className="w-30 h-3" />
          <Skeleton className="w-50 h-3" />
        </div>
        <PerformanceStatusPill.Skeleton />
      </div>
    </>
  );
};
